import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import WestIcon from "@mui/icons-material/West";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Images
import customize from "../../assets/images/Travel/flexible (2).png";
import comprehensive from "../../assets/images/Travel/evaluate.png";
import boosted from "../../assets/images/Travel/rocket.png";
import trust from "../../assets/images/Travel/trust.png";

// Card data type
interface CardData {
  title: string;
  description: string;
  icon: string;
}

const cards: CardData[] = [
  {
    title: "Customizable Assurance Plans",
    description:
      "Tailor-made solutions for your customers. We offer modular and brandable assurance options that fit your audience—whether they need short-term travel protection, extended gadget cover, or full auto peace-of-mind.",
    icon: customize,
  },
  {
    title: "Comprehensive Loyalty Management",
    description:
      "Seamless integration with your digital channels. Partners can integrate via APIs or white-label portals for frictionless policy activation, claims, and customer service—everything online, zero paperwork.",
    icon: comprehensive,
  },
  {
    title: "Boosted Customer Engagement",
    description:
      "Boost revenue with minimal overhead. Earn competitive commissions with full backend support—from claims processing to compliance—so you can focus on growth, not admin.",
    icon: boosted,
  },
  {
    title: "Trusted Assurance Ecosystem",
    description:
      "Backed by data, driven by service. We combine underwritten security with real-time analytics and a proven track record—ensuring you and your customers are protected with confidence.",
    icon: trust,
  },
];

const WhyCardSlider: React.FC = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Card width responsive logic
  const getCardWidth = () => {
    if (isXs) return 280;
    if (isSm) return 320;
    if (isMd) return 360;
    if (isLg) return 380;
    if (isXl) return 400;
    return 340;
  };

  // Card min width responsive logic
  const getMinCardWidth = () => {
    if (isXs) return 240;
    if (isSm) return 280;
    if (isMd) return 320;
    if (isLg) return 340;
    if (isXl) return 360;
    return 280;
  };

  // Auto-scroll for mobile
  useEffect(() => {
    if (isMobile) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
      }, 3500);
      return () => clearInterval(interval);
    }
  }, [isMobile]);

  // Scroll to current card on mobile
  useEffect(() => {
    if (isMobile && containerRef.current) {
      const cardWidth = containerRef.current.offsetWidth;
      containerRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: "smooth",
      });
    }
  }, [currentIndex, isMobile]);

  // For desktop/tablet, scroll by card width
  const scrollTo = (offset: number): void => {
    if (containerRef.current && !isMobile) {
      containerRef.current.scrollBy({
        left: offset,
        behavior: "smooth",
      });
    }
  };

  const nextSlide = (): void => {
    if (isMobile) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % cards.length);
    } else {
      scrollTo(getCardWidth());
    }
  };

  const prevSlide = (): void => {
    if (isMobile) {
      setCurrentIndex(
        (prevIndex) => (prevIndex - 1 + cards.length) % cards.length
      );
    } else {
      scrollTo(-getCardWidth());
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        backgroundColor: "#fff6f1",
        py: { xs: 3, sm: 4, md: 5 },
        px: { xs: 1, sm: 2, md: 4 },
        width: "100%",
      }}
    >
      <div className="max-w-screen-xl" style={{ width: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            px: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            variant="h4"
            className=" main-heading-font-size "
            sx={{
              // fontSize: {
              //   xs: "1.2rem",
              //   sm: "1.5rem",
              //   md: "2rem",
              //   lg: "2.3rem",
              //   xl: "2.5rem",
              // },
              // fontWeight: "bold",
            }}
          >
            Why Partner With Us?
          </Typography>
          <Box>
            <IconButton onClick={prevSlide} aria-label="Previous Slide">
              <WestIcon />
            </IconButton>
            <IconButton onClick={nextSlide} aria-label="Next Slide">
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Card Carousel */}
        <Box
          ref={containerRef}
          sx={{
            display: "flex",
            flexDirection: "row",
            overflowX: isMobile ? "hidden" : "auto",
            scrollBehavior: "smooth",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            gap: isMobile ? 0 : { xs: 1, sm: 2, md: 3 },
            position: "relative",
            width: "100%",
          }}
        >
          {cards.map((card, index) => (
            <Card
              key={index}
              sx={{
                width: isMobile ? "100%" : getCardWidth(),
                minWidth: isMobile ? "100%" : getMinCardWidth(),
                maxWidth: isMobile ? "100%" : 420,
                flexShrink: 0,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 3,
                boxShadow: 3,
                backgroundColor: "#fff",
                mb: 2,
                mx: isMobile ? 0 : { xs: 0.5, sm: 1 },
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                transition: "box-shadow 0.2s",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  width: "100%",
                }}
              >
                <CardMedia
                  className="gif-color-change"
                  component="img"
                  image={card.icon}
                  alt={`${card.title} icon`}
                  sx={{
                    width: { xs: "2.5rem", sm: "3.2rem", md: "3.5rem" },
                    height: { xs: "2.2rem", sm: "2.7rem", md: "3rem" },
                    objectFit: "contain",
                    // filter:
                    //   "invert(48%) sepia(79%) saturate(2476%) hue-rotate(10deg) brightness(100%) contrast(103%)",
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  ml={2}
                  sx={{
                    fontSize: { xs: "1rem", sm: "1.15rem", md: "1.25rem" },
                    flex: 1,
                  }}
                >
                  {card.title}
                </Typography>
              </Box>
              <CardContent sx={{ p: 0, width: "100%" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "0.88rem", sm: "1rem" },
                    textAlign: "justify",
                  }}
                >
                  {card.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Mobile indicators */}
        {isMobile && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 1,
              mt: 2,
            }}
          >
            {cards.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: index === currentIndex ? "#fe7c0b" : "#ccc",
                  transition: "background-color 0.3s ease",
                }}
              />
            ))}
          </Box>
        )}
      </div>
    </Box>
  );
};

export default WhyCardSlider;
